import { Box, PopperProps } from '@mui/material'
import { ReactComponent as CommentIcon } from 'assets/commentIcon.svg'
import { ActionMenuPopper } from 'components/ActionMenuPopper'
import { AnnotationTab } from 'components/AnnotationTab'
import cuid from 'cuid'
import {
  Annotation as DiscoveryAnnotation,
  AnnotationInput,
  Discovery,
} from 'generated/graphql'
import { clone, compact, forEach, map, reduce, sortBy, uniq, zip } from 'lodash'
import { pipe } from 'lodash/fp'
import { pageNumberId } from 'pages/Evaluation/state/usePdfNavigation'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import {
  PDFPageItem,
  PDFPageProxy,
  TextItem,
  TextLayerItemInternal,
} from 'react-pdf'
import { Page } from 'react-pdf/dist/esm/entry.webpack'
import { theme } from 'theme'

import Highlight from './Highlight'
import HighlightBase from './HighlightBase'
import { generateTextItems, reduceOverlappedItems } from './HighlightFunctions'
import { LoadingPage } from './LoadingPage'
import {
  indexTextItems,
  LineMetadata,
  makeAnnotation,
  selectedItems as selectedItemsFromAnnotation,
  SelectedTextItem,
} from './PDFMetadata'

interface Props {
  width: number
  height: number
  scale: number
  pageNumber: number
  render: boolean
  setPageInView: (page: number) => void
  passiveDiscoveries?: Discovery[]
  activeAnnotation?: string
  openAddDiscoveryDrawer: (annotationInput: AnnotationInput) => void
  openViewDiscoveriesDrawer: (discoveryIds: string[]) => void
}
export interface WrappedSelectedTextItem {
  item: SelectedTextItem
  annotation: DiscoveryAnnotation
  discovery: Discovery
}

interface WrappedDiscoveryAnnotation {
  annotation: DiscoveryAnnotation
  discovery: Discovery
}
export interface LineNumberState {
  annotation(selection: Selection | null): DiscoveryAnnotation | undefined
  selectedItems(
    annotation: WrappedDiscoveryAnnotation
  ): WrappedSelectedTextItem[]
  lineNumberLookup: Map<number, LineMetadata[]>
  isValid(itemIndex: number): boolean
}
export interface HighlightData {
  top: number
  left: number
  width: number
  height: number
  annotationIds: string[]
  discoveryIds: string[]
  tabCount?: string[]
}
interface Tabcount {
  tabCount: string[]
  top: number
}
const annotationsOnPage = (
  discoveries: Discovery[],
  currentPage: number
): WrappedDiscoveryAnnotation[] => {
  return discoveries
    .flatMap((discovery) =>
      (discovery?.annotations || []).map((annotation) => ({
        annotation: annotation,
        discovery: discovery,
      }))
    )
    .filter(
      ({ annotation }) =>
        annotation.startPage <= currentPage && annotation.endPage >= currentPage
    )
}

const WrappedPage = ({
  width,
  height,
  scale,
  pageNumber,
  render,
  setPageInView,
  passiveDiscoveries,
  activeAnnotation,
  openAddDiscoveryDrawer,
  openViewDiscoveriesDrawer,
}: Props): JSX.Element => {
  const { ref } = useInView({
    threshold: [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9],
    onChange: (inView, _entry) => {
      if (inView) {
        setPageInView(pageNumber)
      }
    },
  })
  const [viewport, setViewPort] = useState<{
    viewbox: [number, number, number, number]
    defaultSideways: boolean
  }>({
    viewbox: [0, 0, 0, 0],
    defaultSideways: false,
  })
  const [lineMetaData, setLineMetaData] = useState<LineNumberState>()

  const [selectedItems, setSelectedItems] = useState<WrappedSelectedTextItem[]>(
    []
  )

  const [highlightsData, setHighlightsData] = useState<HighlightData[]>([])

  const [anchorEl, setAnchorEl] = useState<PopperProps['anchorEl']>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [tabData, setTabData] = useState<Tabcount[]>([])

  const pageRef = useRef(null)

  const TAB_OFFSET = 40

  const reduceToTextItemSelection = useCallback(
    (annotations: WrappedDiscoveryAnnotation[]) =>
      reduce(
        annotations,
        (
          items: WrappedSelectedTextItem[],
          annotation: WrappedDiscoveryAnnotation
        ) => [...items, ...(lineMetaData?.selectedItems(annotation) || [])],
        []
      ),
    [lineMetaData]
  )

  useEffect(() => {
    const pipeline = pipe(reduceToTextItemSelection, setSelectedItems)
    if (passiveDiscoveries) {
      pipeline(annotationsOnPage(passiveDiscoveries, pageNumber))
    }
  }, [pageNumber, passiveDiscoveries, reduceToTextItemSelection])

  async function calculateLineNumbers(page: PDFPageItem) {
    const viewport = page.getViewport({
      scale: scale,
    })

    const { rotation } = viewport

    setViewPort({
      viewbox: viewport.viewBox,
      defaultSideways: rotation % 180 !== 0,
    })
    return
  }

  const onPageLoadSuccess = (page: PDFPageProxy) => {
    const pageItem = page as unknown as PDFPageItem

    calculateLineNumbers(pageItem)
  }

  const renderDiscoveriesSelection = useCallback(
    (textItem: TextLayerItemInternal) => {
      if (!lineMetaData?.isValid(textItem.itemIndex)) {
        return '' as unknown as JSX.Element
      }

      const itemsToHighlight = selectedItems.filter(
        ({ item }) => item.itemIndex === textItem.itemIndex
      )

      const overlappedItems = sortBy(
        itemsToHighlight,
        ({ item }) => item.startIndex
      ).reduce(reduceOverlappedItems, [])

      const textItems = generateTextItems(overlappedItems)

      const reducedItems = zip(
        textItems.elements,
        overlappedItems.map((item, selectedTextItemIndex) => {
          if (item.parent.annotation.startPage === pageNumber) {
            const annotationIds = item.children.map(
              (child) => child.annotation.id
            )
            const discoveryIds = item.children.map(
              (child) => child.discovery?.id
            )
            return (
              <HighlightBase
                id={`highlightBase-${
                  selectedTextItemIndex + 1
                }-page-${pageNumber}`}
                key={`highlightBase-${
                  selectedTextItemIndex + 1
                }-page-${pageNumber}`}
                item={item}
                setHighlightsData={setHighlightsData}
                pageRef={pageRef}
                annotationIds={annotationIds}
                discoveryIds={discoveryIds}
              />
            )
          }
          return null
        })
      )
        .flat()
        .filter(Boolean)

      if (reducedItems.length > 0) {
        return <React.Fragment>{reducedItems}</React.Fragment>
      }

      return <React.Fragment>{textItem.str}</React.Fragment>
    },

    [selectedItems, lineMetaData, setHighlightsData, pageNumber]
  )
  const onTextItemSuccess = useCallback(
    (items: TextItem[]) => {
      const { yLookup, lineNumberLookup, isValid } = indexTextItems(
        items,
        viewport,
        pageNumber,
        scale
      )

      setLineMetaData({
        annotation: (
          selection: Selection | null
        ): DiscoveryAnnotation | undefined => {
          return selection ? makeAnnotation(selection, yLookup) : undefined
        },
        selectedItems: ({
          annotation,
          discovery,
        }: WrappedDiscoveryAnnotation): WrappedSelectedTextItem[] => {
          return selectedItemsFromAnnotation(annotation, lineNumberLookup).map(
            (item) => ({
              item: item,
              annotation,
              discovery: discovery,
            })
          )
        },
        lineNumberLookup: lineNumberLookup,
        isValid: isValid,
      })
    },
    [viewport, scale, pageNumber]
  )

  useEffect(() => {
    if (!render && highlightsData.length > 0) {
      setHighlightsData([])
      setSelectedItems([])
      setTabData([])
    }
    if (!render) {
      setIsOpen(false)
    }
  }, [highlightsData, pageNumber, render])

  const [newAnnotation, setNewAnnotation] = useState<
    AnnotationInput | undefined
  >()

  const renderActionMenuPopper = () => {
    const ranges = []
    const userSelection = window.getSelection()

    if (userSelection) {
      for (let i = 0; i < userSelection.rangeCount; i++) {
        ranges[i] = userSelection.getRangeAt(i)
      }
    }

    if (
      !userSelection ||
      userSelection?.anchorOffset === userSelection?.focusOffset
    ) {
      return
    }

    const getBoundingClientRect = () =>
      userSelection?.getRangeAt(ranges.length - 1).getBoundingClientRect()

    ranges.length > 0 && setAnchorEl({ getBoundingClientRect })
    setIsOpen(true)

    const annotation = lineMetaData?.annotation(userSelection)
    setNewAnnotation(annotation)
  }

  const handleAddDiscovery = () => {
    if (newAnnotation) {
      const annotationInput = clone(newAnnotation)
      delete annotationInput.id
      openAddDiscoveryDrawer(annotationInput)
    }
    window.getSelection()?.removeAllRanges()
    setIsOpen(false)
  }

  const overlapTabs = useCallback(
    (item: HighlightData) => {
      let discoveriesIds: string[] = []
      let top = item.top
      forEach(highlightsData, (highlight) => {
        if (Math.abs(top - highlight.top) < TAB_OFFSET) {
          top = highlight.top
          discoveriesIds = [...discoveriesIds, ...highlight.discoveryIds]
        }
      })
      return uniq(discoveriesIds)
    },
    [highlightsData]
  )

  useEffect(() => {
    const passiveDiscoveryIds = passiveDiscoveries?.map(
      (discovery) => discovery.id
    )
    setHighlightsData((prev) =>
      prev.filter((highlight) =>
        highlight.discoveryIds.some((id) => passiveDiscoveryIds?.includes(id))
      )
    )
  }, [passiveDiscoveries])

  useEffect(() => {
    setTabData(
      compact(
        map(highlightsData, (item, i) => {
          if (i === 0) {
            return {
              tabCount: overlapTabs(item),
              top: item.top,
            }
          }
          if (item.top - highlightsData[i - 1].top > TAB_OFFSET) {
            return {
              tabCount: overlapTabs(item),
              top: item.top,
            }
          }
          return null
        })
      )
    )
  }, [highlightsData, overlapTabs])

  useEffect(() => {
    setHighlightsData([])
  }, [scale])

  return (
    <>
      <Box
        ref={ref}
        id={pageNumberId(pageNumber)}
        sx={{
          height: height * scale,
          marginBottom: theme.spacing(3),
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        }}
      >
        {render ? (
          <>
            <Box
              style={{ position: 'relative' }}
              ref={pageRef}
              onMouseUp={renderActionMenuPopper}
            >
              {highlightsData.map((highlightData, i) => (
                <Highlight
                  key={`highlight-${i + 1}-page-${pageNumber}`}
                  id={`highlight-${i + 1}-page-${pageNumber}`}
                  highlightData={highlightData}
                  pageNumber={pageNumber}
                  activeAnnotation={activeAnnotation}
                  render={render}
                />
              ))}
              {tabData.map((tab: Tabcount) => (
                <AnnotationTab
                  key={`tab-highlight-${cuid()}`}
                  itemCount={tab.tabCount.length}
                  onClick={() => openViewDiscoveriesDrawer(tab.tabCount)}
                  sx={{
                    position: 'absolute',
                    top: tab.top,
                    left: '-51px',
                  }}
                />
              ))}

              <Page
                pageNumber={pageNumber}
                scale={scale}
                onLoadSuccess={onPageLoadSuccess}
                onGetTextSuccess={onTextItemSuccess}
                customTextRenderer={renderDiscoveriesSelection}
              />
              <ActionMenuPopper
                anchorEl={anchorEl}
                isOpen={render && isOpen}
                setOpen={setIsOpen}
                actionItems={[
                  {
                    itemName: 'Add Discovery',
                    onClick: handleAddDiscovery,
                    icon: (
                      <Box
                        mr={1}
                        height="15px"
                        sx={{
                          '& svg': { fill: 'white' },
                        }}
                      >
                        <CommentIcon />
                      </Box>
                    ),
                  },
                ]}
              />
            </Box>
          </>
        ) : (
          <LoadingPage
            boxSize={{
              width: width * scale,
              height: height * scale,
            }}
          ></LoadingPage>
        )}
      </Box>
    </>
  )
}

export { WrappedPage }
